import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import CustomDropdown from '../ui/CustomDropdown';
import { Bookmark, Loader2, ArrowRight } from 'lucide-react';
import { tagsToCsv } from './skillsUtils';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contentStyle = {
  background: 'var(--dp-bg-glass, rgba(28,28,28,0.85))',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  border: '1px solid var(--dp-border-medium)',
  borderRadius: '16px',
  boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
  maxWidth: '440px',
  overflow: 'visible',
  padding: '24px',
};

/**
 * Salva uma skill do SISTEMA nas skills do usuário (tabela prompts),
 * escolhendo o canal de destino (ou Global). Copia nome, conteúdo,
 * instruções, tutorial, imagens, categoria e tags.
 */
const SaveSkillDialog = ({ skill, canais = [], onClose, onSaved, getDetail }) => {
  const { t } = useTranslation();
  const [canalId, setCanalId] = useState('global');
  const [saving, setSaving] = useState(false);

  if (!skill) return null;

  const canalOptions = [
    { value: 'global', label: t('skills.save_dialog.global') },
    ...canais.map((c) => ({ value: c.id, label: c.nome })),
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      const detail = await getDetail(skill);
      await axios.post(`${API}/prompts`, {
        canal_id: canalId === 'global' ? null : canalId,
        nome: skill.nome,
        categoria: skill.categoria || null,
        conteudo: detail.conteudo || '',
        anotacoes: skill.descricao || null,
        tags: tagsToCsv(skill.tags),
        plataformas: skill.plataformas || [],
        imagens: skill.imagens || [],
        instrucoes: detail.instrucoes || null,
        video_url: detail.video_url || null,
        favorito: false,
      });
      // Salvar nas minhas também conta como uso (prova social).
      axios.post(`${API}/skills/${skill.id}/track-copy`).catch(() => {});
      toast.success(t('skills.save_dialog.saved'));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(t('skills.save_dialog.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent style={contentStyle}>
        <DialogHeader>
          <DialogTitle style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            fontSize: '16px', fontWeight: 700, color: 'var(--dp-text-primary)',
            fontFamily: 'var(--dp-font)', marginBottom: '4px',
          }}>
            <Bookmark size={16} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
            {t('skills.save_dialog.title')}
          </DialogTitle>
        </DialogHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <p style={{
            margin: 0, fontSize: '12.5px', color: 'var(--dp-text-secondary)',
            fontFamily: 'var(--dp-font)', lineHeight: 1.55,
          }}>
            {t('skills.save_dialog.message', { name: skill.nome })}
          </p>

          <div>
            <label style={{
              display: 'block', fontSize: '12px', fontWeight: 500,
              color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)',
              marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px',
            }}>
              {t('skills.save_dialog.channel')}
            </label>
            <CustomDropdown
              options={canalOptions}
              value={canalId}
              onChange={setCanalId}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="dp-btn-cta dp-btn-cta--sm"
              onClick={handleSave}
              disabled={saving}
              style={{ opacity: saving ? 0.6 : 1 }}
            >
              {saving
                ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                : <Bookmark size={14} />}
              {t('skills.save_dialog.save')}
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SaveSkillDialog;
